import type { CSSProperties, ReactNode } from 'react';

export type BadgeTone =
  | 'neutral' | 'accent' | 'brand' | 'danger' | 'warn'
  | 'ok' | 'success' | 'info' | 'purple' | 'slate';

export type BadgeSize = 'sm' | 'md' | 'lg';

export interface BadgeProps {
  tone?: BadgeTone;
  size?: BadgeSize;
  dot?: boolean;
  children?: ReactNode;
  style?: CSSProperties;
  className?: string;
}

const TONE_MAP: Record<BadgeTone, { bg: string; fg: string; bd: string }> = {
  neutral: { bg: 'var(--dg-surface-2)',  fg: 'var(--dg-text-2)',     bd: 'var(--dg-border)' },
  accent:  { bg: 'var(--dg-accent-50)',  fg: 'var(--dg-accent-700)', bd: 'var(--dg-accent-100)' },
  brand:   { bg: 'var(--dg-brand-soft)', fg: 'var(--dg-brand-deep)', bd: '#bfdbfe' },
  danger:  { bg: 'var(--dg-danger-soft)',fg: '#991b1b',              bd: '#fecaca' },
  warn:    { bg: 'var(--dg-warn-soft)',  fg: '#92400e',              bd: '#fde68a' },
  ok:      { bg: 'var(--dg-ok-soft)',    fg: 'var(--dg-accent-700)', bd: 'var(--dg-accent-200)' },
  success: { bg: 'var(--dg-ok-soft)',    fg: 'var(--dg-accent-700)', bd: 'var(--dg-accent-200)' },
  info:    { bg: 'var(--dg-info-soft)',  fg: '#075985',              bd: '#bae6fd' },
  purple:  { bg: '#f3e8ff',              fg: '#6b21a8',              bd: '#e9d5ff' },
  slate:   { bg: '#f1f5f9',              fg: '#334155',              bd: '#cbd5e1' },
};

const SIZE_MAP: Record<BadgeSize, { h: number; px: number; fs: number }> = {
  sm: { h: 18, px: 6, fs: 11 },
  md: { h: 22, px: 8, fs: 12 },
  lg: { h: 26, px: 10, fs: 13 },
};

export function Badge({
  tone = 'neutral',
  size = 'md',
  dot,
  children,
  style,
  className,
}: BadgeProps) {
  const t = TONE_MAP[tone];
  const sz = SIZE_MAP[size];
  return (
    <span
      className={className}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: sz.h,
        padding: `0 ${sz.px}px`,
        background: t.bg,
        color: t.fg,
        border: `1px solid ${t.bd}`,
        borderRadius: 999,
        fontSize: sz.fs,
        fontWeight: 500,
        letterSpacing: '-0.005em',
        whiteSpace: 'nowrap',
        ...style,
      }}
    >
      {dot && <span style={{ width: 6, height: 6, borderRadius: 999, background: t.fg }} />}
      {children}
    </span>
  );
}
